/***
 *
 *   SCORING TEMPLATE HELPERS
 *   Canonical schema fragments and transforms for scoring-enabled
 *   forms (misc.type "risk_assessment" or "edd").
 *
 *   Config lives at the SurveyJS schema root:
 *   scoring: { enabled, threshold_medium, threshold_high }
 *
 *   Rating mode lives at form.misc.scoring_mode:
 *   "manual" (default) | "calculated".
 *
 **********/

export const RATING_QUESTION_NAME = "risk_rating";

// must match model/scoring.js RATING_EXPRESSION — the runner feeds the
// _risk_* variables so this expression previews the calculated rating
export const RATING_EXPRESSION =
  "iif({_risk_score} >= {_risk_threshold_high}, 'high', iif({_risk_score} >= {_risk_threshold_medium}, 'medium', 'low'))";

export const DEFAULT_SCORING = {
  enabled: true,
  threshold_medium: 30,
  threshold_high: 70,
};

export const SCORING_FORM_TYPES = ["risk_assessment", "edd"];

export const SCORING_MODES = ["manual", "calculated"];

export function buildCalculatedRatingQuestion() {
  return {
    type: "expression",
    name: RATING_QUESTION_NAME,
    title: "Risk rating",
    description:
      "Automatically calculated from your scoring rules. Cannot be edited directly.",
    isSystemLocked: true,
    expression: RATING_EXPRESSION,
  };
}

export function buildManualRatingQuestion() {
  return {
    type: "radiogroup",
    name: RATING_QUESTION_NAME,
    title: "Risk rating",
    choices: [
      { value: "low", text: "Low" },
      { value: "medium", text: "Medium" },
      { value: "high", text: "High" },
    ],
  };
}

function cloneSchema(schema) {
  return schema && typeof schema === "object"
    ? JSON.parse(JSON.stringify(schema))
    : {};
}

const CHILD_KEYS = ["pages", "elements", "questions", "templateElements"];

function collectElements(node, output = []) {
  if (!node || typeof node !== "object") return output;

  if (Array.isArray(node)) {
    node.forEach((item) => collectElements(item, output));
    return output;
  }

  if (typeof node.type === "string" && node.name) output.push(node);

  CHILD_KEYS.forEach((key) => {
    if (Array.isArray(node[key])) collectElements(node[key], output);
  });

  return output;
}

export function hasQuestionNamed(schema, name) {
  return collectElements(schema, []).some((q) => q.name === name);
}

export function normalizeScoringConfig(config) {
  const medium = Number(config?.threshold_medium);
  const high = Number(config?.threshold_high);

  return {
    enabled: true,
    threshold_medium: Number.isFinite(medium)
      ? medium
      : DEFAULT_SCORING.threshold_medium,
    threshold_high: Number.isFinite(high)
      ? high
      : DEFAULT_SCORING.threshold_high,
  };
}

/*
 * ensureCalculatedSchema()
 * Returns a copy of the schema carrying the canonical scoring config
 * with the locked risk_rating expression question injected if missing.
 */
export function ensureCalculatedSchema(schema, config) {
  const base = cloneSchema(schema);
  base.scoring = normalizeScoringConfig(config || base.scoring);

  if (!Array.isArray(base.pages) || base.pages.length === 0) {
    base.pages = [{ name: "page1", elements: [] }];
  }

  const hasRatingQuestion = collectElements(base, []).some(
    (q) => q.name === RATING_QUESTION_NAME && q.type === "expression"
  );

  if (!hasRatingQuestion) {
    const lastPage = base.pages[base.pages.length - 1];
    lastPage.elements = Array.isArray(lastPage.elements)
      ? lastPage.elements
      : [];
    lastPage.elements.push(buildCalculatedRatingQuestion());
  }

  return base;
}

/*
 * removeCalculatedScoring()
 * Returns a copy of the schema without the scoring config and without
 * the locked risk_rating expression question. A manual radiogroup
 * named risk_rating is left untouched.
 */
export function removeCalculatedScoring(schema) {
  const base = cloneSchema(schema);
  delete base.scoring;

  const strip = (node) => {
    if (!node || typeof node !== "object") return;

    CHILD_KEYS.forEach((key) => {
      if (!Array.isArray(node[key])) return;
      node[key] = node[key].filter(
        (el) =>
          !(el && el.name === RATING_QUESTION_NAME && el.type === "expression")
      );
      node[key].forEach(strip);
    });
  };

  strip(base);
  return base;
}

/*
 * insertManualRatingQuestion()
 * Returns a copy of the schema with a plain (unlocked, unscored)
 * low/medium/high radiogroup named risk_rating appended to the last
 * page — the manual-mode rating convention. No-op when a question
 * with that name already exists.
 */
export function insertManualRatingQuestion(schema) {
  const base = cloneSchema(schema);

  if (hasQuestionNamed(base, RATING_QUESTION_NAME)) return base;

  if (!Array.isArray(base.pages) || base.pages.length === 0) {
    base.pages = [{ name: "page1", elements: [] }];
  }

  const lastPage = base.pages[base.pages.length - 1];
  lastPage.elements = Array.isArray(lastPage.elements)
    ? lastPage.elements
    : [];
  lastPage.elements.push(buildManualRatingQuestion());

  return base;
}
